import React, { useState, useEffect } from 'react';
import { contactsApi, companiesApi, importApi } from '../lib/api';
import { formatDate, getInitials } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter 
} from '../components/ui/dialog';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../components/ui/select';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, MagnifyingGlass, Pencil, Trash, Phone, Envelope, Building, Upload, Download 
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import logger from '../lib/logger';

export default function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    company_id: '',
    position: '',
    notes: '',
    tags: [],
    lead_score: 0
  });

  useEffect(() => {
    fetchContacts();
    fetchCompanies();
  }, [search]);

  const fetchContacts = async () => {
    try {
      const response = await contactsApi.getAll({ search, limit: 100 });
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesApi.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      logger.error('Failed to fetch companies:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingContact) {
        await contactsApi.update(editingContact.id, formData);
        toast.success('Contact updated');
      } else {
        await contactsApi.create(formData);
        toast.success('Contact created');
      }
      setIsDialogOpen(false);
      resetForm();
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    try {
      await contactsApi.delete(id);
      toast.success('Contact deleted');
      fetchContacts();
    } catch (error) {
      toast.error('Failed to delete contact');
    }
  };

  const openEditDialog = (contact) => {
    setEditingContact(contact);
    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email || '',
      phone: contact.phone || '',
      company_id: contact.company_id || '',
      position: contact.position || '',
      notes: contact.notes || '',
      tags: contact.tags || [],
      lead_score: contact.lead_score || 0
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingContact(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      company_id: '',
      position: '',
      notes: '',
      tags: [],
      lead_score: 0
    });
  };

  const getLeadScoreColor = (score) => {
    if (score >= 80) return 'bg-emerald-500';
    if (score >= 60) return 'bg-blue-500';
    if (score >= 40) return 'bg-amber-500';
    return 'bg-zinc-500';
  };

  const handleCsvImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const response = await importApi.importContacts(formData);
      toast.success(`Imported ${response.data.imported} contacts`);
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} rows had errors`);
      }
      setIsImportOpen(false);
      setImportFile(null);
      fetchContacts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const csv = 'first_name,last_name,email,phone,position,lead_score,tags,notes\nJohn,Doe,john@example.com,+1234567890,CEO,80,sales;vip,Important client\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contacts_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="contacts-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Contacts</h1>
          <p className="text-muted-foreground text-sm mt-1">{contacts.length} contacts</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setIsImportOpen(true)} 
            className="rounded-sm"
            data-testid="import-contacts-btn"
          >
            <Upload size={18} className="mr-2" />
            Import CSV
          </Button>
          <Button 
            onClick={() => { resetForm(); setIsDialogOpen(true); }} 
            className="rounded-sm"
            data-testid="add-contact-btn"
          >
            <Plus size={18} className="mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input
          placeholder="Search contacts..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-sm"
          data-testid="search-contacts"
        />
      </div>

      {/* Contacts Table */}
      <div className="bg-card border border-border rounded-sm overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Contact</th>
              <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Company</th>
              <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Contact Info</th>
              <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Score</th>
              <th className="text-left p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Created</th>
              <th className="text-right p-4 text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">Loading...</td>
              </tr>
            ) : contacts.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-8 text-center text-muted-foreground">No contacts found</td>
              </tr>
            ) : (
              contacts.map((contact) => (
                <tr key={contact.id} className="border-b border-border hover:bg-muted/30 transition-colors" data-testid={`contact-row-${contact.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9 rounded-sm">
                        <AvatarFallback className="rounded-sm bg-primary/10 text-primary font-medium">
                          {getInitials(`${contact.first_name} ${contact.last_name}`)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium">{contact.first_name} {contact.last_name}</p>
                        {contact.position && (
                          <p className="text-xs text-muted-foreground">{contact.position}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="p-4">
                    {contact.company_name ? (
                      <div className="flex items-center gap-2">
                        <Building size={16} className="text-muted-foreground" />
                        <span className="text-sm">{contact.company_name}</span>
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      {contact.email && (
                        <div className="flex items-center gap-2 text-sm">
                          <Envelope size={14} className="text-muted-foreground" />
                          <a href={`mailto:${contact.email}`} className="hover:text-primary">{contact.email}</a>
                        </div>
                      )}
                      {contact.phone && (
                        <div className="flex items-center gap-2 text-sm">
                          <Phone size={14} className="text-muted-foreground" />
                          <span>{contact.phone}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="p-4">
                    <div className="flex items-center gap-2">
                      <div className={`h-2 w-2 rounded-full ${getLeadScoreColor(contact.lead_score)}`}></div>
                      <span className="text-sm font-mono">{contact.lead_score}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-muted-foreground">
                    {formatDate(contact.created_at)}
                  </td>
                  <td className="p-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditDialog(contact)}
                        data-testid={`edit-contact-${contact.id}`}
                      >
                        <Pencil size={16} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(contact.id)}
                        className="text-destructive hover:text-destructive"
                        data-testid={`delete-contact-${contact.id}`}
                      >
                        <Trash size={16} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContact ? 'Edit Contact' : 'Add Contact'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  required
                  data-testid="contact-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  required
                  data-testid="contact-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="contact-email"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                data-testid="contact-phone"
              />
            </div>
            <div className="space-y-2">
              <Label>Company</Label>
              <Select
                value={formData.company_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, company_id: value === "none" ? "" : value })}
              >
                <SelectTrigger data-testid="contact-company">
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>{company.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Position</Label>
              <Input
                value={formData.position}
                onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                data-testid="contact-position"
              />
            </div>
            <div className="space-y-2">
              <Label>Lead Score (0-100)</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={formData.lead_score}
                onChange={(e) => setFormData({ ...formData, lead_score: parseInt(e.target.value) || 0 })}
                data-testid="contact-lead-score"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                data-testid="contact-notes"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="contact-submit">
                {editingContact ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import CSV Dialog */}
      <Dialog open={isImportOpen} onOpenChange={setIsImportOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Import Contacts from CSV</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Upload a CSV file with columns: first_name, last_name, email, phone, position, lead_score, tags, notes
            </p>
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template">
              <Download size={14} className="mr-2" />
              Download Template
            </Button>
            <div className="space-y-2">
              <Label>CSV File</Label>
              <Input
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
            </div>
            {importFile && (
              <p className="text-sm text-muted-foreground">Selected: {importFile.name}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsImportOpen(false)}>Cancel</Button>
            <Button onClick={handleCsvImport} disabled={!importFile || importing} data-testid="import-submit">
              {importing ? 'Importing...' : 'Import'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
